/**
 * 📐 DASHBOARD GRID
 * ════════════════════════════════════════════════════════════════════════
 * 
 * Dashboard editable estilo Power BI / Looker Studio
 * - Layout inicial inteligente
 * - Drag & resize libre
 * - Permite superposición (overlap)
 * - Control total del usuario sobre el canvas
 * 
 * FILOSOFÍA:
 * "Lienzo profesional donde el usuario tiene el control total"
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Responsive, WidthProvider, Layout as RGLLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './EditorialDashboardGrid.css';
import { EditorialLayoutEngine, WidgetWithLayout } from '../layout/EditorialLayoutEngine';
import WidgetRenderer from './WidgetRenderer';
import type { Dashboard } from '../../../types/dashboard';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface EditorialDashboardGridProps {
  dashboard: Dashboard;
  editable?: boolean;
  onLayoutChange?: (widgets: WidgetWithLayout[]) => void;
  onWidgetConfigSave?: (widgetId: string, config: Record<string, any>) => Promise<void> | void;
  onWidgetDelete?: (widgetId: string) => Promise<void> | void;
}

/**
 * Dashboard Grid editable
 */
export const EditorialDashboardGrid: React.FC<EditorialDashboardGridProps> = ({
  dashboard,
  editable = true,
  onLayoutChange,
  onWidgetConfigSave,
  onWidgetDelete
}) => {
  
  // ════════════════════════════════════════════════════════════════
  // ESTADO
  // ════════════════════════════════════════════════════════════════
  
  // Generar layout inicial automáticamente
  const [widgets, setWidgets] = useState<WidgetWithLayout[]>(() => {
    console.log('📐 [DashboardGrid] Generando layout inicial...');
    return EditorialLayoutEngine.generateLayout(dashboard.widgets);
  });
  
  // Sincronizar widgets cuando cambia el dashboard
  useEffect(() => {
    console.log('📐 [DashboardGrid] Dashboard cambió, regenerando layout...');
    const newWidgets = EditorialLayoutEngine.generateLayout(dashboard.widgets);
    setWidgets(newWidgets);
  }, [dashboard.widgets]);
  
  // Estado para menú contextual
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [customizeWidgetId, setCustomizeWidgetId] = useState<string | null>(null);
  
  // Cerrar menú al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };
    
    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);
  
  // ════════════════════════════════════════════════════════════════
  // HANDLERS
  // ════════════════════════════════════════════════════════════════
  
  /**
   * Handle drag/resize
   */
  const handleLayoutChange = useCallback((newLayout: RGLLayout[]) => {
    if (!editable) return;
    
    const updatedWidgets = EditorialLayoutEngine.updateLayout(widgets, newLayout);
    setWidgets(updatedWidgets);
    
    if (onLayoutChange) {
      onLayoutChange(updatedWidgets);
    }
  }, [widgets, editable, onLayoutChange]);
  
  /**
   * Handle widget deletion
   */
  const handleDeleteWidget = useCallback(async (widgetId: string) => {
    if (!editable) return;
    
    // Simple confirmation
    if (window.confirm('¿Eliminar este widget?')) {
      const updatedWidgets = widgets.filter(w => w.id !== widgetId);
      setWidgets(updatedWidgets);
      
      if (onLayoutChange) {
        onLayoutChange(updatedWidgets);
      }
      
      // Llamar al callback de eliminación si existe
      if (onWidgetDelete) {
        await onWidgetDelete(widgetId);
      }
    }
    
    // Cerrar menú
    setOpenMenuId(null);
  }, [widgets, editable, onLayoutChange, onWidgetDelete]);
  
  /**
   * Toggle menú de opciones
   */
  const toggleMenu = useCallback((widgetId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === widgetId ? null : widgetId);
  }, [openMenuId]);

  const handleCustomizeWidget = useCallback((widgetId: string) => {
    setCustomizeWidgetId(widgetId);
    setOpenMenuId(null);
  }, []);

  const handleSaveWidgetConfig = useCallback(async (widgetId: string, config: Record<string, any>) => {
    setWidgets((prevWidgets) =>
      prevWidgets.map((widget) =>
        widget.id === widgetId
          ? {
              ...widget,
              title: typeof config.title === 'string' && config.title.trim()
                ? config.title.trim()
                : widget.title,
              config: { ...config }
            }
          : widget
      )
    );

    if (onWidgetConfigSave) {
      await onWidgetConfigSave(widgetId, config);
    }
  }, [onWidgetConfigSave]);
  
  // ════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════
  
  // Convertir a formato react-grid-layout
  const layouts = {
    lg: widgets.map(w => w.gridLayout)
  };
  
  console.log('📐 [DashboardGrid] Renderizando:', {
    totalWidgets: widgets.length,
    metrics: widgets.filter(w => w.type === 'metric').length,
    charts: widgets.filter(w => ['bar', 'pie', 'line'].includes(w.type)).length,
    tables: widgets.filter(w => w.type === 'table').length
  });
  
  return (
    <div className="editorial-dashboard-grid">
      
      {/* Grid Layout */}
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
        rowHeight={60}
        isDraggable={editable}
        isResizable={editable}
        draggableCancel=".table-sort-button, .table-container button, input, textarea, select"
        onLayoutChange={handleLayoutChange}
        margin={[16, 16]}
        containerPadding={[24, 24]}
        compactType="vertical" // Reorganizar al eliminar widgets
        preventCollision={false} // Permitir superposición durante drag
      >
        {widgets.map((widget) => (
          <div 
            key={widget.id}
            className={`
              grid-item
              ${widget.isEmpty ? 'is-empty' : ''}
              ${widget.isWeak ? 'is-weak' : ''}
            `}
          >
            {editable && (
              <div 
                className="widget-menu-container" 
                ref={openMenuId === widget.id ? menuRef : null}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="widget-menu-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleMenu(widget.id, e);
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  title="Opciones"
                >
                  ⋮
                </button>
                
                {openMenuId === widget.id && (
                  <div 
                    className="widget-dropdown-menu"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="widget-menu-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleCustomizeWidget(widget.id);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <span className="menu-icon">🎛️</span>
                      <span>Personalizar</span>
                    </button>
                    <button
                      className="widget-menu-item delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeleteWidget(widget.id);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <span className="menu-icon">🗑️</span>
                      <span>Eliminar widget</span>
                    </button>
                    {/* Agregar más opciones aquí en el futuro */}
                  </div>
                )}
              </div>
            )}
            <div className="widget-content">
              {renderWidget(widget, {
                isConfigOpen: customizeWidgetId === widget.id,
                onCloseConfig: () => setCustomizeWidgetId(null),
              }, handleSaveWidgetConfig)}
            </div>
          </div>
        ))}
      </ResponsiveGridLayout>
      
    </div>
  );
};

/**
 * Renderiza un widget según su tipo
 */
function renderWidget(
  widget: WidgetWithLayout,
  configOverrides: { isConfigOpen: boolean; onCloseConfig: () => void },
  onSaveConfig?: (widgetId: string, config: Record<string, any>) => Promise<void> | void
): React.ReactNode {
  
  // Widget con datos débiles
  if (widget.isWeak || widget.isEmpty) {
    return (
      <div className="widget-weak-wrapper">
        <div className="weak-badge">
          ⚠️ Datos insuficientes
        </div>
        {renderWidgetByType(widget, configOverrides, onSaveConfig)}
      </div>
    );
  }
  
  // Widget normal
  return renderWidgetByType(widget, configOverrides, onSaveConfig);
}

/**
 * Renderiza widget según tipo
 */
function renderWidgetByType(
  widget: WidgetWithLayout,
  configOverrides: { isConfigOpen: boolean; onCloseConfig: () => void },
  onSaveConfig?: (widgetId: string, config: Record<string, any>) => Promise<void> | void
): React.ReactNode {
  const supportedTypes = ['metric', 'bar', 'line', 'pie', 'table'];
  if (!supportedTypes.includes(widget.type)) {
    return (
      <div className="widget-unknown">
        <p>Widget tipo desconocido: {widget.type}</p>
      </div>
    );
  }

  const config = (widget as { config?: Record<string, any> }).config
    ?? (widget.metadata as { config?: Record<string, any> } | undefined)?.config
    ?? {};

  return (
    <WidgetRenderer
      widget={{
        id: widget.id,
        title: widget.title,
        type: widget.type,
        data: widget.data,
        config
      }}
      isConfigOpen={configOverrides.isConfigOpen}
      onCloseConfig={configOverrides.onCloseConfig}
      onSaveConfig={onSaveConfig ? (nextConfig) => onSaveConfig(widget.id, nextConfig) : undefined}
    />
  );
}

export default EditorialDashboardGrid;
